import { Skeleton, Typography } from '@cobliteam/ui/components';
import Card, { CardContent } from '@cobliteam/ui/components/Card';
import Popover, { PopoverContent, PopoverIcon, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import { colors } from '@cobliteam/ui/theme';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';

type RawKpiDirection = 'up' | 'down' | 'neutral';
type RawKpiUnit = 'km' | 'minutes' | 'percent' | 'number' | string;

interface RawKpiTrend {
  delta: number;
  deltaPercentage: number; // e.g. -0.49 means -49%
  direction: RawKpiDirection;
  deltaColor?: string;
}

interface RawKpiSparkline {
  values: number[];
  labels?: string[];
  color: string; // Required when sparkline exists
  areaColor?: string;
}

interface RawKpi {
  id: string;
  title: string;
  current: number;
  previous?: number;
  unit?: RawKpiUnit;
  precision?: number;
  subtitle?: string;
  explanation?: string;
  trend?: RawKpiTrend;
  sparkline?: RawKpiSparkline;
  betterWhen?: 'higher' | 'lower';
}

interface KpiSparklinePoint {
  name: string;
  value: number;
}

interface KpiCardViewModel {
  id: string;
  label: string;
  delta?: number;
  valueText: string;
  previous?: string;
  sublabel?: string;
  deltaPercentText?: string;
  deltaAbsoluteText?: string;
  deltaColor?: string;
  betterWhen?: 'higher' | 'lower';
  sparkline?: KpiSparklinePoint[];
  sparkStroke?: string;
  sparkFill?: string;
  explanation?: string;
}

const numberFormatter = (precision = 0) =>
  new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

const formatWithUnit = ({ value, unit, precision }: { value: number; unit?: RawKpiUnit; precision?: number }) => {
  const p = typeof precision === 'number' ? precision : 0;

  if (unit === 'percent') {
    return `${numberFormatter(p).format(value)}%`;
  }

  const formatted = numberFormatter(p).format(value);

  if (!unit || unit === 'number') return formatted;

  if (unit === 'minutes' && value > 60) return `${Math.floor(value / 60)} h`;
  if (unit === 'minutes') return `${formatted} min`;
  return `${formatted} ${unit}`;
};

const formatSignedAbsolute = ({ delta, unit, precision }: { delta: number; unit?: RawKpiUnit; precision?: number }) => {
  const sign = delta > 0 ? '+' : '';
  const formatted = formatWithUnit({ value: Math.abs(delta), unit, precision });
  return `(${sign}${delta < 0 ? '-' : ''}${formatted})`;
};

const getDeltaColor = (trend?: RawKpiTrend, betterWhen: 'higher' | 'lower' = 'higher') => {
  // If explicit color is provided, use it
  if (trend?.deltaColor) return trend.deltaColor;

  // If no trend or neutral, return gray
  if (!trend || trend.direction === 'neutral') {
    return colors.gray[10];
  }

  // Determine if the trend is good or bad based on betterWhen
  const isGood = (betterWhen === 'higher' && trend.direction === 'up') || (betterWhen === 'lower' && trend.direction === 'down');

  return isGood ? colors.success[9] : colors.danger[9];
};

const toViewModel = (kpi: RawKpi): KpiCardViewModel => {
  const precision = typeof kpi.precision === 'number' ? kpi.precision : 0;
  const betterWhen = kpi.betterWhen ?? 'higher';
  const deltaColor = getDeltaColor(kpi.trend, betterWhen);

  const percent = typeof kpi.trend?.deltaPercentage === 'number' ? kpi.trend.deltaPercentage * 100 : undefined;
  const deltaPercentText = percent !== undefined ? `${percent > 0 ? '+' : ''}${numberFormatter(1).format(percent)}%` : undefined;
  const deltaAbsoluteText = typeof kpi.trend?.delta === 'number' ? formatSignedAbsolute({ delta: kpi.trend.delta, unit: kpi.unit, precision }) : undefined;

  const sparkline = kpi.sparkline?.values?.length
    ? kpi.sparkline.values.map((value, idx) => ({
        name: kpi.sparkline?.labels?.[idx] ?? `Ponto ${idx + 1}`,
        value,
      }))
    : undefined;

  return {
    id: kpi.id,
    label: kpi.title,
    delta: kpi.trend?.delta,
    valueText: formatWithUnit({ value: kpi.current, unit: kpi.unit, precision }),
    sublabel: kpi.subtitle,
    deltaPercentText,
    deltaAbsoluteText,
    deltaColor,
    betterWhen,
    sparkline,
    explanation: kpi.explanation,
    previous: formatWithUnit({ value: kpi.previous ?? 0, unit: kpi.unit, precision }),
    sparkStroke: kpi.sparkline?.color || colors.link.DEFAULT,
    sparkFill: kpi.sparkline?.areaColor || kpi.sparkline?.color || colors.link.DEFAULT,
  };
};

const KpiCard = ({ item }: { item: KpiCardViewModel }) => {
  const deltaColor = item.deltaColor ?? colors.gray[10];
  const sparkData = item.sparkline ?? [];
  const sparkStroke = item.sparkStroke ?? colors.link.DEFAULT;
  const sparkFill = item.sparkFill ?? colors.link.DEFAULT;
  const hasDelta = !!item?.delta;

  return (
    <div className='flex flex-col justify-between rounded-lg border border-gray-4 bg-white p-4 shadow-sm'>
      <div className='flex items-center justify-between'>
        <Typography variant='text-md-medium'>{item.label}</Typography>
        {item.explanation && (
          <Popover>
            <PopoverTrigger asChild>
              <div>
                <PopoverIcon />
              </div>
            </PopoverTrigger>
            <PopoverContent className='w-48' showArrow={false}>
              <Typography variant='text-sm' color='secondary'>
                {item.explanation}
              </Typography>
            </PopoverContent>
          </Popover>
        )}
      </div>

      <div className='mt-2 flex items-center justify-between gap-2'>
        <div className='flex flex-col'>
          <Typography variant='heading-xl' className='text-gray-12'>
            {item.valueText}
          </Typography>
          {item.sublabel && hasDelta && (
            <Typography variant='caption-xs' color='secondary'>
              {item.sublabel}
            </Typography>
          )}
        </div>

        {hasDelta && (
          <div className='flex flex-col self-end text-right'>
            {(item.deltaPercentText || item.deltaAbsoluteText) && (
              <div className='flex flex-wrap items-center justify-end gap-1 text-right'>
                {item.deltaPercentText && (
                  <Typography variant='text-sm-medium' style={{ color: deltaColor }}>
                    {item.deltaPercentText}
                  </Typography>
                )}
                {item.deltaAbsoluteText && (
                  <Typography variant='text-sm' style={{ color: deltaColor }}>
                    {item.deltaAbsoluteText}
                  </Typography>
                )}
              </div>
            )}
            <Typography variant='text-sm' className='mt-1 text-gray-10'>
              Anterior: {item.previous}
            </Typography>
          </div>
        )}
      </div>

      {hasDelta && !!sparkData.length && (
        <div className='mt-3 h-10 min-h-[40px] w-full'>
          <ResponsiveContainer width='100%' height={40}>
            <AreaChart data={sparkData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
              <Area
                type='monotone'
                dataKey='value'
                stroke={sparkStroke}
                fill={sparkFill || sparkStroke}
                fillOpacity={0.1}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                activeDot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export const PerformanceDataToolSkeleton = () => {
  return (
    <Card className='my-4'>
      <CardContent>
        <div className='flex size-8 items-center justify-center rounded-md bg-gray-3' />
        <div className='mt-4 flex flex-col gap-2'>
          <Skeleton className='h-5 w-44' />
          <Skeleton className='h-4 w-32' />
        </div>
      </CardContent>
    </Card>
  );
};

const PerformanceDataTool = ({ part }: { part: any }) => {
  const output = part?.output as any;
  const itemsRaw = output?.cards ?? [];

  if (!itemsRaw || itemsRaw.length === 0) {
    return null;
  }

  const items = itemsRaw.map(toViewModel);

  return (
    <div className='my-4 grid grid-cols-2 gap-3'>
      {items.map((item: KpiCardViewModel) => (
        <KpiCard key={item.id || item.label} item={item} />
      ))}
    </div>
  );
};

export default PerformanceDataTool;
